import React, { useState } from 'react';
import { Row, Col, Card, Badge, Button, Modal, Form, Alert } from 'react-bootstrap';
import { BsCalendar } from 'react-icons/bs';
import emailjs from '@emailjs/browser';

interface NewsItem {
  id: number;
  type: 'Événement' | 'Actualité';
  date: string;
  title: string;
  description: string;
  fullContent?: string;
  image: string;
  email?: string;
  location?: string;
  organizer?: string;
}

interface ContactFormData {
  name: string;
  email: string;
  telephone: string;
  subject: string;
  message: string;
}

// Static news data
const staticNewsItems: NewsItem[] = [
  {
    id: 1,
    type: 'Événement',
    date: '15 Juin 2025',
    title: 'Journée Portes Ouvertes',
    description: 'Découvrez nos locaux, rencontrez nos enseignants et nos étudiants lors de notre prochaine journée portes ouvertes.',
    fullContent: 'Rejoignez-nous pour une journée portes ouvertes complète ! Vous pourrez visiter nos locaux modernes, rencontrer nos enseignants passionnés et discuter avec les étudiants de leurs expériences à Horizons School. Sessions de questions-réponses, visites guidées, et rafraîchissements seront disponibles.',
    image: '/images/event.jpeg',
    email: 'events@horizonsschool.com',
    location: 'Campus Marrakech',
    organizer: 'Horizons School'
  },
  {
    id: 2,
    type: 'Actualité',
    date: '5 Juin 2025',
    title: 'Nouveau Partenariat International',
    description: 'Horizons School signe un nouveau partenariat avec une prestigieuse université européenne pour des échanges étudiants.',
    fullContent: 'Fiers d\'annoncer notre nouveau partenariat stratégique avec l\'Université de Lyon ! Ce partenariat offre à nos étudiants l\'opportunité de participer à des échanges académiques, des stages internships, et des collaborations de recherche. Les étudiants sélectionnés pourront passer un semestre en France tout en poursuivant leurs études.',
    image: '/images/2.jpg',
    email: 'international@horizonsschool.com',
    location: 'Bureau International',
    organizer: 'Département Partenariats'
  },
  {
    id: 3,
    type: 'Actualité',
    date: '1 Juin 2025',
    title: 'LICENCE PROFESSIONNELLE',
    description: 'Notre programme de Licence Professionnelle vous offre une formation complète avec plusieurs spécialisations au choix.',
    fullContent: 'Le programme de Licence Professionnelle d\'Horizons School propose une formation de 3 ans combinant théorie et pratique. Les étudiants bénéficient de stages en entreprise, de projets réels, et d\'un accompagnement personnalisé pour leur carrière. Spécialisations disponibles : Management des organisations, Gestion des ressources humaines, Commerce International, et Gestion comptable et financière.',
    image: '/images/1.jpg',
    email: 'admissions@horizonsschool.com',
    location: 'Campus Marrakech',
    organizer: 'Département Académique'
  }
];

const NewsSection: React.FC = () => {
  const [selectedItem, setSelectedItem] = useState<NewsItem | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [contactForm, setContactForm] = useState<ContactFormData>({
    name: '',
    email: '',
    telephone: '',
    subject: '',
    message: '',
  });
  const [formSubmitting, setFormSubmitting] = useState(false);
  const [formMessage, setFormMessage] = useState<{
    type: 'success' | 'error';
    text: string;
  } | null>(null);

  React.useEffect(() => {
    // Initialize EmailJS with environment variable
    const publicKey = process.env.REACT_APP_EMAILJS_PUBLIC_KEY;
    if (publicKey) {
      emailjs.init(publicKey);
    }
  }, []);

  const getBadgeVariant = (type: string) => {
    switch (type) {
      case 'Événement':
        return 'primary';
      case 'Actualité':
        return 'success';
      default:
        return 'secondary';
    }
  };

  const handleViewMore = (item: NewsItem) => {
    setSelectedItem(item);
    setShowModal(true);
    setFormMessage(null);
    setContactForm({
      name: '',
      email: '',
      telephone: '',
      subject: `Demande concernant: ${item.title}`,
      message: '',
    });
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setSelectedItem(null);
    setFormMessage(null);
  };

  const handleFormChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setContactForm((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleContactFormSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormSubmitting(true);
    setFormMessage(null);

    try {
      const emailServiceId =
        process.env.REACT_APP_EMAILJS_SERVICE_ID || '';
      const emailTemplateSchoolId =
        process.env.REACT_APP_EMAILJS_TEMPLATE_SCHOOL_ID || '';
      const emailTemplateReplyId =
        process.env.REACT_APP_EMAILJS_TEMPLATE_REPLY_ID || '';
      const emailPublicKey =
        process.env.REACT_APP_EMAILJS_PUBLIC_KEY || '';

      if (!emailServiceId || !emailTemplateSchoolId || !emailTemplateReplyId || !emailPublicKey) {
        throw new Error(
          'Email configuration is missing. Please check your environment variables.'
        );
      }

      const templateParamsToSchool = {
        to_email: selectedItem?.email,
        nom: contactForm.name,
        email: contactForm.email,
        telephone: contactForm.telephone,
        sujet: contactForm.subject,
        message: contactForm.message,
      };

      // Send email to school
      await emailjs.send(
        emailServiceId,
        emailTemplateSchoolId,
        templateParamsToSchool,
        {
          publicKey: emailPublicKey,
        }
      );

      // Send reply email to user (don't fail if this one errors)
      try {
        const templateParamsReply = {
          to_email: contactForm.email,
          nom: contactForm.name,
          sujet: contactForm.subject,
        };

        await emailjs.send(
          emailServiceId,
          emailTemplateReplyId,
          templateParamsReply,
          {
            publicKey: emailPublicKey,
          }
        );
      } catch (replyError) {
        console.warn('Warning: Reply email could not be sent:', replyError);
        // Don't throw - the main email was sent successfully
      }

      setFormMessage({
        type: 'success',
        text: 'Message envoyé avec succès! Un email de confirmation a été envoyé à votre adresse email.',
      });
      setContactForm({
        name: '',
        email: '',
        telephone: '',
        subject: `Demande concernant: ${selectedItem?.title}`,
        message: '',
      });
    } catch (error) {
      setFormMessage({
        type: 'error',
        text:
          error instanceof Error
            ? error.message
            : 'Erreur lors de l\'envoi. Veuillez réessayer.',
      });
    } finally {
      setFormSubmitting(false);
    }
  };

  return (
    <>
      <Row className="g-4">
        {staticNewsItems.map((item, index) => (
          <Col md={4} key={item.id}>
            <Card 
              className="h-100 shadow-sm border-0 news-card"
              data-aos="fade-up" 
              data-aos-delay={`${index * 100}`}
            >
              <div className="position-relative">
                <Card.Img 
                  variant="top" 
                  src={item.image} 
                  alt={item.title}
                  className="news-card-img"
                  style={{ height: '200px', objectFit: 'cover', objectPosition: index === 0 ? 'bottom' : 'center' }}
                />
                <Badge 
                  bg={getBadgeVariant(item.type)}
                  className="position-absolute top-0 end-0 m-2"
                >
                  {item.type}
                </Badge>
              </div>
              <Card.Body className="d-flex flex-column">
                <div className="d-flex align-items-center mb-2">
                  <BsCalendar className="text-primary me-2" />
                  <small className="text-muted">{item.date}</small>
                </div>
                <Card.Title className="h5 mb-3">{item.title}</Card.Title>
                
                <Card.Text className="text-muted">
                  {item.description}
                </Card.Text>
                
                <div className="mt-auto pt-3">
                  <Button 
                    variant="primary" 
                    className="w-100"
                    onClick={() => handleViewMore(item)}
                  >
                    Voir plus
                  </Button>
                </div>
              </Card.Body>
            </Card>
          </Col>
        ))}
      </Row>

      {/* Modal for detailed news view */}
      <Modal show={showModal} onHide={handleCloseModal} size="lg" centered>
        {selectedItem && (
          <>
            <Modal.Header closeButton>
              <Modal.Title>{selectedItem.title}</Modal.Title>
            </Modal.Header>
            <Modal.Body>
              <div className="mb-4">
                <img 
                  src={selectedItem.image} 
                  alt={selectedItem.title}
                  className="img-fluid rounded"
                  style={{ maxHeight: '300px', objectFit: 'cover', width: '100%', objectPosition: selectedItem.id === 1 ? 'bottom' : 'center' }}
                />
              </div>

              <div className="mb-3">
                <Badge bg={getBadgeVariant(selectedItem.type)} className="me-2">
                  {selectedItem.type}
                </Badge>
                <small className="text-muted d-flex align-items-center mt-2">
                  <BsCalendar className="me-2" />
                  {selectedItem.date}
                </small>
              </div>

              <div className="mb-4">
                <h5>Détails</h5>
                <p className="text-muted">
                  {selectedItem.fullContent || selectedItem.description}
                </p>
              </div>

              {selectedItem.email && (
                <div className="border-top pt-3">
                  <h5 className="mb-3">Nous contacter</h5>
                  
                  {formMessage && (
                    <Alert 
                      variant={formMessage.type === 'success' ? 'success' : 'danger'}
                      dismissible
                      onClose={() => setFormMessage(null)}
                      className="mb-3"
                    >
                      {formMessage.text}
                    </Alert>
                  )}

                  <Form onSubmit={handleContactFormSubmit}>
                    <Form.Group className="mb-3">
                      <Form.Label>Nom complet *</Form.Label>
                      <Form.Control
                        type="text"
                        name="name"
                        value={contactForm.name}
                        onChange={handleFormChange}
                        placeholder="Votre nom"
                        required
                        disabled={formSubmitting}
                      />
                    </Form.Group>

                    <Form.Group className="mb-3">
                      <Form.Label>Email *</Form.Label>
                      <Form.Control
                        type="email"
                        name="email"
                        value={contactForm.email}
                        onChange={handleFormChange}
                        placeholder="votre.email@exemple.com"
                        required
                        disabled={formSubmitting}
                      />
                    </Form.Group>

                    <Form.Group className="mb-3">
                      <Form.Label>Téléphone *</Form.Label>
                      <Form.Control
                        type="tel"
                        name="telephone"
                        value={contactForm.telephone}
                        onChange={handleFormChange}
                        placeholder="Votre numéro de téléphone"
                        required
                        disabled={formSubmitting}
                      />
                    </Form.Group>

                    <Form.Group className="mb-3">
                      <Form.Label>Sujet *</Form.Label>
                      <Form.Control
                        type="text"
                        name="subject"
                        value={contactForm.subject}
                        onChange={handleFormChange}
                        placeholder="Sujet du message"
                        required
                        disabled={formSubmitting}
                      />
                    </Form.Group>

                    <Form.Group className="mb-3">
                      <Form.Label>Message *</Form.Label>
                      <Form.Control
                        as="textarea"
                        rows={4}
                        name="message"
                        value={contactForm.message}
                        onChange={handleFormChange}
                        placeholder="Votre message..."
                        required
                        disabled={formSubmitting}
                      />
                    </Form.Group>

                    <Button 
                      variant="primary" 
                      type="submit"
                      className="w-100"
                      disabled={formSubmitting}
                    >
                      {formSubmitting ? 'Envoi en cours...' : 'Envoyer le message'}
                    </Button>
                  </Form>
                </div>
              )}
            </Modal.Body>
            <Modal.Footer>
              <Button variant="secondary" onClick={handleCloseModal}>
                Fermer
              </Button>
            </Modal.Footer>
          </>
        )}
      </Modal>
    </>
  );
};

export default NewsSection;